import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { useMemo, useState } from 'react';
import {
  useDeleteProviderModel,
  useImportVideogenModels,
  useProvider,
  useProviderModels,
  useSaveProviderModel,
} from '../../api/hooks';
import type { ProviderModel } from '../../api/types';
import { extractErrorMessage } from '../../api/client';
import { CategorySelect } from '../CategorySelect';
import { jsonRule } from '../JsonField';
import { parseJsonText, toJsonText, paramsSchemaValidator } from '../../utils/format';
import { resolveModelParams } from './modelParams';

export default function ProviderModelsTab({ providerId }: { providerId: string }) {
  const { message } = AntApp.useApp();
  const provider = useProvider(providerId);
  const models = useProviderModels(providerId);
  const save = useSaveProviderModel(providerId);
  const del = useDeleteProviderModel(providerId);
  const importVideogen = useImportVideogenModels(providerId);

  // Videogen co endpoint GET /api/v1/models tra TAT CA model + tham so dong -> nut "Load model"
  // se nap thang tu nguon (cac provider khac chi reload danh sach DB hien co).
  const isVideogen = provider.data?.adapter === 'videogen-webhook';
  const onLoadModels = async () => {
    if (!isVideogen) {
      models.refetch();
      return;
    }
    try {
      const r = await importVideogen.mutateAsync();
      await models.refetch();
      message.success(`Đã nạp ${r.imported} model từ nguồn (${r.created} mới, ${r.updated} cập nhật).`);
      // Hiển thị đúng tham số nhận được từ nguồn cho từng model (để soi tham số thừa/thiếu).
      const lines = (r.models || []).map((m) => `• ${m.code}: ${m.params.join(', ') || '(không có)'}`).join('\n');
      if (lines) console.info('[Videogen] Tham số từ nguồn theo model:\n' + lines);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderModel | null>(null);
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active' });
    setOpen(true);
  };

  const openEdit = (r: ProviderModel) => {
    setEditing(r);
    const c = (r.config as Record<string, any>) || {};
    form.setFieldsValue({
      ...r,
      config: toJsonText(r.config),
      paramsSchema: toJsonText(r.paramsSchema),
      pollInitialDelaySec: c.pollInitialDelayMs != null ? c.pollInitialDelayMs / 1000 : undefined,
      pollIntervalSec: c.pollIntervalMs != null ? c.pollIntervalMs / 1000 : undefined,
      pollMaxWaitMin: c.pollMaxWaitMs != null ? c.pollMaxWaitMs / 60000 : undefined,
    });
    setOpen(true);
  };

  // Dien san schema goi y (fallback theo code) de admin chinh nhanh khi o dang trong.
  const fillSuggestedSchema = () => {
    const code = form.getFieldValue('code');
    const submitEndpoint = form.getFieldValue('submitEndpoint');
    if (!code) {
      message.warning('Nhập mã model trước để gợi ý schema');
      return;
    }
    const suggested = resolveModelParams({ code, submitEndpoint });
    form.setFieldsValue({ paramsSchema: toJsonText(suggested) });
  };

  const onSubmit = async () => {
    const v = await form.validateFields();
    const parsed = parseJsonText(v.config ?? '');
    // Gop cau hinh polling (do admin nhap o cac o rieng) vao config JSON.
    const config: Record<string, any> = { ...((parsed.value as Record<string, any>) || {}) };
    if (v.pollInitialDelaySec != null && v.pollInitialDelaySec !== '')
      config.pollInitialDelayMs = Math.round(Number(v.pollInitialDelaySec) * 1000);
    if (v.pollIntervalSec != null && v.pollIntervalSec !== '')
      config.pollIntervalMs = Math.round(Number(v.pollIntervalSec) * 1000);
    if (v.pollMaxWaitMin != null && v.pollMaxWaitMin !== '')
      config.pollMaxWaitMs = Math.round(Number(v.pollMaxWaitMin) * 60000);
    const parsedSchema = parseJsonText(v.paramsSchema ?? '');
    const data: Partial<ProviderModel> = {
      code: v.code,
      name: v.name,
      category: v.category,
      description: v.description,
      submitEndpoint: v.submitEndpoint,
      statusEndpoint: v.statusEndpoint,
      status: v.status,
      config: Object.keys(config).length ? config : undefined,
      // Mang rong / trong -> null (xoa schema, quay ve fallback theo code).
      paramsSchema: Array.isArray(parsedSchema.value) && parsedSchema.value.length
        ? (parsedSchema.value as ProviderModel['paramsSchema'])
        : null,
    };
    try {
      await save.mutateAsync({ modelId: editing?.id, data });
      message.success(editing ? 'Đã cập nhật model' : 'Đã tạo model');
      setOpen(false);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const columns = [
    { title: 'Mã (model)', dataIndex: 'code', key: 'code', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Tên', dataIndex: 'name', key: 'name' },
    { title: 'Danh mục', dataIndex: 'category', key: 'category', render: (v: string) => v || '—' },
    {
      title: 'Endpoint',
      dataIndex: 'submitEndpoint',
      key: 'submitEndpoint',
      render: (v: string) =>
        v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v: string) => (v === 'active' ? <Tag color="success">Bật</Tag> : <Tag>Tắt</Tag>),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_: unknown, r: ProviderModel) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Xóa model?" okText="Xóa" cancelText="Hủy" onConfirm={() => del.mutate(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Typography.Text type="secondary">
          Mỗi model là một “mẫu” mà nhà cung cấp này phục vụ (vd gpt-image-2). Dùng để gom vào Nhóm model.
        </Typography.Text>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            loading={models.isFetching || importVideogen.isPending}
            onClick={onLoadModels}
          >
            {isVideogen ? 'Nạp model từ nguồn' : 'Load model'}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Thêm model
          </Button>
        </Space>
      </Space>
      <ModelsByCategory data={models.data ?? []} loading={models.isLoading} columns={columns} />

      <Drawer
        title={editing ? 'Sửa model' : 'Thêm model'}
        width={520}
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setOpen(false)}>Hủy</Button>
            <Button type="primary" loading={save.isPending} onClick={onSubmit}>
              Lưu
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Space style={{ display: 'flex' }} align="start" size="large">
            <Form.Item
              name="code"
              label="Mã model"
              tooltip="Giá trị gửi vào trường `model` của request"
              rules={[{ required: true, message: 'Nhập mã model' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="vd: gpt-image-2" />
            </Form.Item>
            <Form.Item name="status" label="Trạng thái" style={{ flex: 1 }}>
              <Select options={[{ value: 'active', label: 'Bật' }, { value: 'disabled', label: 'Tắt' }]} />
            </Form.Item>
          </Space>
          <Form.Item name="name" label="Tên" rules={[{ required: true, message: 'Nhập tên' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category" label="Danh mục">
            <CategorySelect placeholder="vd: Tạo ảnh / Tạo video / Tạo văn bản" />
          </Form.Item>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="submitEndpoint" label="Submit Endpoint (ghi đè nhà cung cấp)">
            <Input placeholder="/api/v1/developer/generate/image" />
          </Form.Item>
          <Form.Item name="statusEndpoint" label="Status Endpoint (tùy chọn)">
            <Input placeholder="/api/v1/developer/status/{id}" />
          </Form.Item>

          <Divider orientation="left" style={{ marginTop: 8 }}>
            Thời gian polling
          </Divider>
          <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
            Chờ ban đầu (không polling) để tiết kiệm tài nguyên, sau đó poll theo chu kỳ cho đến
            khi đạt thời gian tối đa. VD ảnh: chờ 40s / chu kỳ 5s; điều khiển chuyển động: chờ 240s
            / chu kỳ 20s.
          </Typography.Paragraph>
          <Space size="large" wrap>
            <Form.Item name="pollInitialDelaySec" label="Chờ ban đầu (giây)">
              <InputNumber min={0} style={{ width: 150 }} placeholder="vd 40" />
            </Form.Item>
            <Form.Item name="pollIntervalSec" label="Chu kỳ poll (giây)">
              <InputNumber min={1} style={{ width: 150 }} placeholder="vd 5" />
            </Form.Item>
            <Form.Item name="pollMaxWaitMin" label="Tối đa (phút)">
              <InputNumber min={1} style={{ width: 150 }} placeholder="vd 4" />
            </Form.Item>
          </Space>

          <Form.Item
            name="config"
            label="Config (JSON)"
            tooltip="Ghi đè config nhà cung cấp cho riêng model này (vd { async: false, outputPath: 'text' })"
            rules={[jsonRule]}
          >
            <Input.TextArea rows={4} style={{ fontFamily: 'monospace' }} placeholder="{ }" />
          </Form.Item>

          <Divider orientation="left" style={{ marginTop: 8 }}>
            Tham số đầu vào (paramsSchema)
          </Divider>
          <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
            Mô tả các tham số đầu vào của model (mảng JSON). Đây là nguồn để trang kiểm thử render
            form VÀ để phần mềm thứ 3 đọc qua <code>GET /api/v1/catalog</code>. Để trống = tự suy
            theo mã model (fallback). Mỗi phần tử: <code>{'{ name, label, type, options?, default?, required?, max?, showWhen? }'}</code>.
          </Typography.Paragraph>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button size="small" onClick={fillSuggestedSchema}>Gợi ý theo mã model</Button>
          </Form.Item>
          <Form.Item name="paramsSchema" label="paramsSchema (JSON array)" rules={[{ validator: paramsSchemaValidator }]}>
            <Input.TextArea rows={8} style={{ fontFamily: 'monospace' }} placeholder='[ { "name": "prompt", "label": "Prompt", "type": "textarea", "required": true } ]' />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}

// Hiển thị model theo từng tab danh mục cho dễ kiểm tra.
function ModelsByCategory({
  data,
  loading,
  columns,
}: {
  data: ProviderModel[];
  loading: boolean;
  columns: any[];
}) {
  const cats = useMemo(() => {
    const set = new Set<string>();
    data.forEach((m) => set.add(m.category || 'Khác'));
    return Array.from(set);
  }, [data]);

  const renderTable = (rows: ProviderModel[]) => (
    <Table<ProviderModel>
      rowKey="id"
      size="small"
      loading={loading}
      dataSource={rows}
      columns={columns}
      pagination={false}
    />
  );

  if (cats.length <= 1) return renderTable(data);

  const items = [
    { key: '__all', label: `Tất cả (${data.length})`, children: renderTable(data) },
    ...cats.map((c) => {
      const rows = data.filter((m) => (m.category || 'Khác') === c);
      return { key: c, label: `${c} (${rows.length})`, children: renderTable(rows) };
    }),
  ];
  return <Tabs items={items} />;
}
